import { FC, useState } from "react";
import { Box, Typography } from "@material-ui/core";
import { Cancel as CancelIcon } from "@material-ui/icons";
import { usePostHog } from "posthog-js/react";

import {
  AllAccessDiscountTooltip,
  Button,
  ErrorMessage,
  LabelField,
  Modal,
  ROIEquation,
} from "components";
import { useAssetDetailData } from "containers/AssetDetail/hooks";
import { useTimer } from "hooks";
import { getReturnOnInvestment } from "lib/finance";
import {
  formatCurrencyAmount,
  formatDateRange,
  formatPercentage,
} from "lib/format";
import { DEFAULT_ERROR_MESSAGE } from "services/constants";
import { ResaleInfo } from "services/listings/types";
import { rejectListingOffer } from "services/offers";
import { ListingOffer } from "services/offers/types";
import theme from "theme";
import { circleIconSize } from "theme/constants";

interface RejectOfferModalProps {
  onClose: () => void;
  listingId: number;
  isOwner: boolean;
  offer: ListingOffer;
  resaleInfo: ResaleInfo;
  royxTake: number;
}

const titleByStep = ["Reject Offer", "Offer Rejected"];

const RejectOfferModal: FC<RejectOfferModalProps> = ({
  onClose,
  listingId,
  isOwner,
  offer,
  resaleInfo,
  royxTake,
}) => {
  const netProceeds = Number(offer.amount) * (1 - royxTake);

  const { listing } = useAssetDetailData(listingId);
  const currentTime = useTimer();
  const posthog = usePostHog();

  const [step, setStep] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleClose = () => {
    onClose();
    setTimeout(() => setStep(0), 100);
  };

  const handleReject = async () => {
    try {
      setIsLoading(true);
      setErrorMessage("");
      await rejectListingOffer(offer, listingId, posthog);
      setStep(s => s + 1);
    } catch (e) {
      setErrorMessage(e instanceof Error ? e.message : DEFAULT_ERROR_MESSAGE);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal title={titleByStep[step]} open onClose={handleClose}>
      {step === 0 && (
        <>
          <Typography component="div" gutterBottom>
            <Box fontSize={13}>
              {isOwner ? (
                <>
                  Rejecting this offer will end the negotiation – you will not
                  be able to make a counteroffer to this buyer unless they make
                  a new offer.
                </>
              ) : (
                <>
                  You will not be able to accept this offer. If you haven’t made
                  a counteroffer to this seller counteroffer, the negotiation
                  will end here.
                </>
              )}
            </Box>
          </Typography>
          {errorMessage && (
            <Box my={2}>
              <ErrorMessage title="Request Failed" description={errorMessage} />
            </Box>
          )}
          <Box my={2} textAlign="center">
            {isOwner && (
              <>
                <Box my={2}>
                  <LabelField
                    label="Offer Expires"
                    value={`In ${formatDateRange(
                      currentTime,
                      offer.expiration,
                      2,
                    )}`}
                  />
                </Box>
                <Box my={2}>
                  <LabelField
                    label="Offer Amount (Buyer Pays)"
                    value={formatCurrencyAmount("USD", offer.amount)}
                  />
                </Box>
                <Box my={2}>
                  <LabelField
                    label={`\u2212 Marketplace Fee (${formatPercentage(
                      royxTake,
                    )})`}
                    value={`\u2212${formatCurrencyAmount(
                      "USD",
                      Number(offer.amount) * royxTake,
                    )}`}
                    tooltipText={
                      listing?.kind !== "direct_listing" ? (
                        <AllAccessDiscountTooltip />
                      ) : undefined
                    }
                    interactiveTooltip
                  />
                </Box>
                <Box my={2}>
                  <LabelField
                    label="= Your Sale Proceeds"
                    value={formatCurrencyAmount("USD", netProceeds)}
                  />
                </Box>
                {!!resaleInfo && !!resaleInfo?.last_update && (
                  <Box my={2}>
                    <LabelField
                      label="Return On Investment"
                      tooltipText={<ROIEquation />}
                      value={formatPercentage(
                        getReturnOnInvestment(
                          resaleInfo.royalties_received,
                          netProceeds,
                          resaleInfo.purchased_amount,
                          resaleInfo.buyer_fee,
                        ),
                      )}
                    />
                  </Box>
                )}
              </>
            )}
            <Box my={2}>
              <Button
                customColor="red"
                size="large"
                variant="contained"
                fullWidth
                onClick={handleReject}
                disabled={isLoading}
                isLoading={isLoading}
              >
                Reject Offer
              </Button>
            </Box>
          </Box>
        </>
      )}
      {step === 1 && (
        <>
          <Typography component="div">
            <Box textAlign="center">
              <CancelIcon
                style={{
                  fontSize: circleIconSize.big,
                  color: theme.custom.palette.red.main,
                }}
              />
            </Box>
            <Box fontSize={15} textAlign="center" m={2}>
              Offer rejected
            </Box>
          </Typography>
        </>
      )}
    </Modal>
  );
};

export default RejectOfferModal;
